/**
 * ActionIcon — the ops console's row-action button, ONE shared component (Q18/Q19; Jeff, 31 October 2026).
 *
 * ⭐ WHY THIS FILE EXISTS. Jeff, on the Alerts table's text buttons: "why is this a repeated failure?" Because the
 * pattern — an icon button coloured by meaning, with its own hover tooltip, side by side in an Actions column — lived
 * only as code copy-forked page to page (`routes/admin/users/shared.tsx`, `routes/admin/grading/shared.tsx`). The UI
 * standard never stated it and nothing checked it, so every new table that did not happen to copy from those pages
 * invented its own text buttons. The standard now states it (`admin-portal-ui-standards.md` §6), a CI test checks it
 * (`test/ops-table-actions.test.ts`), and new code uses this.
 *
 * The copy-forked versions may stay where a stream's pages need to diverge (the common-catalogue model); this one is
 * for everything else. Colours by meaning live in `styles.css` beside `.portal-action-icon-btn`:
 *   view / events — neutral · acknowledge / assign / review — blue · clear / resume — teal ·
 *   pause / detach — amber · remove — red.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from './Tooltip';

/** Every glyph a row action can use — 24×24, stroked, `currentColor`. */
export const ACTION_ICONS = {
  view: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
  /** Show the underlying events — a list. */
  events: <><path d="M8 6h13M8 12h13M8 18h13" /><circle cx="3.5" cy="6" r="1" /><circle cx="3.5" cy="12" r="1" /><circle cx="3.5" cy="18" r="1" /></>,
  /** Acknowledge — a bell crossed out: silences it. */
  acknowledge: <><path d="M13.7 21a2 2 0 01-3.4 0" /><path d="M18.6 13A17 17 0 0118 8" /><path d="M6.3 6.3A6 6 0 006 8c0 7-3 9-3 9h14" /><path d="M18 8a6 6 0 00-9.3-5" /><path d="M2 2l20 20" /></>,
  /** Un-acknowledge — the bell, back on. */
  unacknowledge: <><path d="M13.7 21a2 2 0 01-3.4 0" /><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" /></>,
  /** Clear — a tick in a circle: resolves it. */
  clear: <><circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" /></>,
  pause: <path d="M9.5 5.5v13M14.5 5.5v13" />,
  resume: <polygon points="7 4.5 19 12 7 19.5 7 4.5" />,
  assign: <><circle cx="9" cy="7" r="3.2" /><path d="M3.5 20a6 6 0 0111 0" /><path d="M15 12l2 2 4-4" /></>,
  detach: <><circle cx="9" cy="7" r="3.2" /><path d="M3.5 20a6 6 0 0111 0" /><path d="M15.5 11.5h6" /></>,
  review: <><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></>,
  remove: <><path d="M4 7h16" /><path d="M9 7V4h6v3" /><path d="M6 7l1 13h10l1-13" /></>,
  /** Keep it as it is — a tick. */
  keep: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  /** Reset a password — a key. */
  key: <><circle cx="8" cy="15" r="4" /><path d="M10.8 12.2L20 3" /><path d="M16 7l3 3" /></>,
  /** Reverse — an undo arrow. */
  undo: <><path d="M9 14L4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 010 11H11" /></>,
  /** Close a gate — a closed lock. */
  lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></>,
  /** Open a gate — an open lock. */
  unlock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 017.5-2" /></>,
  /** Mark / clear a flag (beta). */
  flag: <><path d="M5 21V4" /><path d="M5 4h12l-2.5 4L17 12H5" /></>,
  /** Grant — a plus. */
  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
} satisfies Record<string, ReactNode>;

export type ActionIconName = keyof typeof ACTION_ICONS;

/**
 * One row action: an icon button (or link), coloured by `act`, with a hover tooltip naming the action. `label` is the
 * accessible name (say WHICH row: "Acknowledge Email stuck in limbo"); `tip` is the sentence the tooltip shows.
 */
export function ActionIcon({ act, icon, label, tip, onClick, href, disabled, testId, expanded }: {
  act: string;
  icon: ActionIconName;
  label: string;
  tip: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  /** `data-testid` on the button itself. */
  testId?: string;
  /** For a disclosure (Show events, provenance): `aria-expanded`. */
  expanded?: boolean;
}) {
  const svg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ACTION_ICONS[icon]}
    </svg>
  );
  return (
    <Tooltip content={tip} aria={tip}>
      {href
        ? <Link to={href} className="portal-action-icon-btn" data-act={act} aria-label={label} data-testid={testId}>{svg}</Link>
        : <button type="button" className="portal-action-icon-btn" data-act={act} aria-label={label} onClick={onClick} disabled={disabled} data-testid={testId} aria-expanded={expanded}>{svg}</button>}
    </Tooltip>
  );
}

/** The Actions cell's row of icon buttons — side by side, one gap, never wrapping. */
export function ActionsRow({ children }: { children: ReactNode }) {
  return <span className="portal-actions-row">{children}</span>;
}
